<template>
  <div class="cs-page-container">
    <h1>Alat Customer Service</h1>

    <div class="section add-user-section">
      <h3>Tambah Nasabah Baru</h3>
      <UserForm 
        :submit-function="handleCreateUser" 
        submit-text="Daftarkan Nasabah"
        @success="onUserCreated"
      />
    </div>
    
    <!-- Form Pencarian -->
    <div class="search-box">
      <input v-model="searchEmail" @keyup.enter="handleSearch" placeholder="Cari nasabah berdasarkan email..." />
      <button @click="handleSearch" :disabled="isLoading" class="btn btn-primary">
        {{ isLoading ? 'Mencari...' : 'Cari' }}
      </button>
    </div>

    <!-- Indikator Loading & Pesan Error -->
    <div v-if="isLoading" class="loading-message">Mencari nasabah...</div>
    <div v-if="error" class="error-message">{{ error }}</div>

    <!-- Hasil Pencarian (hanya ditampilkan jika ada hasil) -->
    <div v-if="searchResult" class="result-container">
      <h2>Hasil Pencarian untuk: <strong>{{ searchResult.email }}</strong></h2>
      
      <!-- Detail Nasabah -->
      <div class="user-details section">
        <h3>Detail Nasabah</h3>
        <p><strong>Nama:</strong> {{ searchResult.full_name }}</p>
        <p><strong>Status Akun:</strong> {{ searchResult.status }}</p>
      </div>

      <!-- Daftar Rekening (Buku Tabungan) -->
      <div class="accounts-list section">
        <h3>Daftar Rekening</h3>
        <table v-if="searchResult.accounts.length > 0">
          <thead><tr><th>No. Rekening</th><th>Tipe</th><th>Saldo</th><th>Status</th></tr></thead>
          <tbody>
            <tr v-for="account in searchResult.accounts" :key="account.id">
              <td>{{ account.account_number }}</td>
              <td>{{ account.account_type }}</td>
              <td class="amount">Rp {{ formatCurrency(account.balance) }}</td>
              <td>
                <span class="status-badge" :class="`status-${account.status.toLowerCase()}`">{{ account.status }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else>Nasabah ini tidak memiliki rekening.</p>
      </div>

      <!-- Form Buka Rekening Baru -->
      <div class="add-account-section section">
        <h3>Buka Rekening Baru untuk {{ searchResult.full_name }}</h3>
        <div class="form-inline">
          <select v-model="newAccountType" class="form-control">
            <option value="Tabungan">Tabungan</option>
            <option value="Giro">Giro</option>
            <option value="Tabungan Berjangka">Tabungan Berjangka</option>
          </select>
          <button @click="handleOpenAccount" class="btn btn-primary">Buka Rekening</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import api from '@/services/api';
import { useToast } from 'vue-toastification';
import UserForm from '@/components/UserForm.vue'; 

const toast = useToast();
const searchEmail = ref('');
const isLoading = ref(false);
const error = ref(null);
const searchResult = ref(null);
const newAccountType = ref('Tabungan');

const handleCreateUser = (userData) => {
  return api.createUser(userData);
};

const onUserCreated = () => {
  toast.success("Nasabah baru berhasil didaftarkan & menunggu persetujuan.");
  // Opsional: kosongkan form pencarian jika ada
  searchEmail.value = '';
  searchResult.value = null;
};

const handleSearch = async () => {
  if (!searchEmail.value) {
    toast.warning("Silakan masukkan email nasabah.");
    return;
  }
  isLoading.value = true;
  error.value = null;
  searchResult.value = null;
  try {
    const response = await api.findUserByEmail(searchEmail.value);
    searchResult.value = response.data;
  } catch (err) {
    error.value = err.response?.data?.detail || 'Gagal mencari nasabah.';
    toast.error(error.value);
  } finally {
    isLoading.value = false;
  }
};

const handleOpenAccount = async () => {
  if (!searchResult.value) return;
  try {
    const accountData = {
      owner_id: searchResult.value.id,
      account_type: newAccountType.value
    };
    await api.adminCreateAccount(accountData);
    toast.success(`Rekening baru untuk ${searchResult.value.full_name} berhasil dibuat & menunggu persetujuan.`);
    await handleSearch(); // Refresh hasil pencarian
  } catch (err) {
    toast.error(err.response?.data?.detail || "Gagal membuka rekening.");
  }
};

const formatCurrency = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? '0.00' : num.toLocaleString('id-ID');
};
</script>
  
<style scoped>
  .cs-page-container { padding: 1.5rem; }
  .search-box { display: flex; gap: 10px; margin-bottom: 2rem; }
  .search-box input { flex-grow: 1; padding: 0.75rem; border: 1px solid #ccc; border-radius: 4px; }
  .result-container { display: flex; flex-direction: column; gap: 2rem; }
  .section { padding: 1.5rem; border-radius: 8px; background-color: #f8f9fa; border: 1px solid #e9ecef; }
  table { width: 100%; border-collapse: collapse; background-color: white; }
  th, td { padding: 0.75rem; text-align: left; border-bottom: 1px solid #ddd; }
  th { background-color: #e9ecef; }
  .amount { text-align: right; }
  .form-inline { display: flex; gap: 1rem; align-items: center; }
  .form-control { padding: 0.5rem; border-radius: 4px; border: 1px solid #ccc; }
  /* Salin style badge dari DashboardPage.vue */
  .status-badge { display: inline-block; padding: 0.25em 0.6em; font-size: 0.75rem; font-weight: 700; border-radius: 0.25rem; color: #fff; }
  .status-active { background-color: #28a745; }
  .status-pending { background-color: #ffc107; color: #212529; }
  .status-rejected { background-color: #dc3545; }
  .status-suspended { background-color: #6c757d; }
  .separator {
    border: none;
    border-top: 1px solid #ddd;
    margin: 2.5rem 0;
  }
  .add-user-section {
    background-color: #e9f7ef; /* Warna latar yang sedikit berbeda */
  }
</style>